import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { ColumnsType } from 'antd/es/table';
import {
  message, Modal, Table,
} from 'antd';
import styles from './WalletList.module.scss';
import { formatLongStr } from '../../../../utils/format';
import { getLoginType } from '../../../../utils/auth';
import AddWalletAsset from '../AddWalletAsset';
import ModalFooter from '../../../../components/ModalFooter';
import LabelButton from '../../../../components/LabelButton';
import { WalletVcItem } from '../../../../utils/vc/types';
import { LoginType } from '../../../../enum';
import OntoScan from '../../../../components/LoginUser/ConnectWallet/OntoScan';
import { ScoreResult } from '../../../../types/api';
import { useGlobalState } from '../../../../store';
import Cyano from '../../../../utils/DApp/Cyano';
import Onto from '../../../../utils/DApp/Onto';
import Clickable from '../../../../components/Clickable';

/**
 * wallet address manage.
 */
const WalletList: React.FC = () => {
  const { t } = useTranslation();
  const [dApp] = useGlobalState('dApp');
  const [addressList, setAddressList] = useGlobalState('addressList');
  const [ontoScanShowing, setOntoScanShowing] = useState<boolean>(false);
  const [addWalletShowing, setAddWalletShowing] = useState<boolean>(false);
  const [deleteShowing, setDeleteShowing] = useState<boolean>(false);
  const [deleteTarget, setDeleteTarget] = useState<WalletVcItem | null>(null);
  const setScoreLoading = useGlobalState('scoreLoading')[1];

  const showDelete = (target: WalletVcItem) => {
    setDeleteShowing(true);
    setDeleteTarget(target);
  };

  const closeDelete = () => {
    setDeleteTarget(null);
    setDeleteShowing(false);
  };

  const refreshScore = () => {
    if (!dApp) {
      return;
    }
    setScoreLoading(true);
    dApp.fetchScore().finally(() => {
      setScoreLoading(false);
    });
  };

  const submitDelete = async () => {
    if ((dApp instanceof Cyano) && deleteTarget) {
      try {
        await dApp.removeAddress2Cyano(deleteTarget);
        refreshScore();
        setAddressList(dApp.addressList);
        message.success(t('delete_success'));
        closeDelete();
      } catch (e) {
        message.error(e.message);
      }
    }
  };

  const refreshHandler = () => {
    if (!dApp) {
      return;
    }
    if (dApp instanceof Cyano) {
      dApp.loadAddressList().then(() => {
        setAddressList(dApp.addressList);
        refreshScore();
        message.success(t('refresh_success'));
      }).catch((e) => {
        message.error(e.message || t('refresh_fail'));
      });
    } else {
      setOntoScanShowing(true);
    }
  };

  const closeScanRefresh = () => {
    setOntoScanShowing(false);
  };

  const refreshFail = (result: ScoreResult) => {
    message.success(result.errorInfo);
    closeScanRefresh();
  };

  const onOntoScanSuccess = (result: ScoreResult) => {
    if (dApp instanceof Onto) {
      dApp.setScoreResultId(result.oscoreId)
        .setJwtStr(result.addressCredential)
        .setAddressListWithJwt();
      refreshScore();
      setAddressList(dApp.addressList);
      message.success(t('refresh_success'));
      closeScanRefresh();
    }
  };

  const walletColumn: ColumnsType<WalletVcItem> = [
    {
      title: t('address'),
      key: 'address',
      dataIndex: 'address',
      render: (val) => formatLongStr(val, '***', 10, 6),
    },
    {
      title: t('wallet_type'),
      key: 'chain',
      dataIndex: 'chain',
      render: (val) => val,
    },
    {
      title: t('signature_auth'),
      key: 'sig',
      dataIndex: 'sig',
      render: (signature, row) => {
        if (signature) {
          return <div>{formatLongStr(signature)}</div>;
        }
        return <div>-</div>;
      },
    },
  ];

  if (dApp && dApp instanceof Cyano) {
    walletColumn.push(
      {
        title: t('operate'),
        key: 'operate',
        render: (val, row) => (
          <div>
            <Clickable event={() => showDelete(row)} label={t('delete')}>
              <i className="iconfont icondelete" />
            </Clickable>
          </div>
        ),
      },
    );
  }

  return (
    <div className="g-container">
      <div className={styles.titleWrapper}>
        {
          dApp
          && (
          <div className={styles.titleButton}>
            <LabelButton label={t('refresh_wallet')} onClick={refreshHandler} />
          </div>
          )
        }
        {
          getLoginType() === LoginType.Cyano
          && (
          <div className={styles.titleButton}>
            <LabelButton label={t('add_wallet')} onClick={() => setAddWalletShowing(true)} />
          </div>
          )
        }

        <h2 className="g-sub-title first">{t('wallet_assets')}</h2>
      </div>
      <div className="g-card">
        <div className="g-table-wrapper">
          <Table
            columns={walletColumn}
            rowKey={(record) => record.address}
            dataSource={addressList}
            pagination={false}
          />
        </div>
        <Modal
          title={t('add_wallet_title')}
          visible={addWalletShowing}
          onCancel={() => setAddWalletShowing(false)}
          footer={null}
          destroyOnClose
          width={540}
        >
          <AddWalletAsset
            onCancel={() => setAddWalletShowing(false)}
            onSuccess={() => {
              setAddWalletShowing(false);
            }}
          />
        </Modal>
        <Modal title={t('delete')} visible={deleteShowing} onCancel={closeDelete} footer={null} width={540}>
          <div className={styles.modalContainer}>
            <div>{t('confirm_delete_wallet')}</div>
            <ModalFooter onCancel={closeDelete} onConfirm={submitDelete} />
          </div>
        </Modal>
        <Modal
          title={t('refresh_wallet')}
          visible={ontoScanShowing}
          onCancel={closeScanRefresh}
          footer={null}
          width={450}
        >
          <OntoScan
            onSuccess={onOntoScanSuccess}
            onFail={refreshFail}
            onBack={closeScanRefresh}
          />
        </Modal>
      </div>
    </div>
  );
};

export default WalletList;
